import { For, Index, Show, type JSX } from "solid-js";

import { Icon } from "~/icon";

import { ArchArt } from "../art/arch_art";
import styles from "../deck.module.css";
import { useDeckWords } from "../deck_words";
import type { OptionsSlide as OptionsSlideData } from "../types";

/** The patient's choices side by side: each plan with its treatments, visits and chair time, what's good to know and
 *  what to keep in mind, and "Wait for now" while an If it waits strip shows. The doctor's pick is marked. With each
 *  option's teeth shown, a plan's column opens with a small drawing of the teeth it works on. */
export function OptionsSlide(props: { slide: OptionsSlideData }): JSX.Element {
  const words = useDeckWords();
  return (
    <>
      <h2 class={styles["s-h"]}>{props.slide.headline}</h2>
      <ol class={styles["opts"]} style={{ "--n": Math.max(props.slide.options.length, 1) }}>
        <Index each={props.slide.options}>
          {(option) => (
            <li
              class={styles["opt"]}
              data-pick={option().recommended ? "" : undefined}
              data-wait={option().key === "wait" ? "" : undefined}
            >
              <Show when={option().recommended}>
                <span class={styles["opt-pick"]}>{words().t("doctorsRecommendation")}</span>
              </Show>
              <Show when={option().arch}>
                {(arch) => (
                  <div class={styles["opt-arch"]}>
                    <ArchArt
                      arch={arch()}
                      mode="mini"
                      numbers={false}
                      title={words().t("teethIn", { name: option().title })}
                    />
                  </div>
                )}
              </Show>
              <h3 class={styles["opt-t"]}>{option().title}</h3>
              <Show
                when={
                  option().treatments.length > 0 &&
                  option().treatments.join(", ") !== option().title
                }
              >
                <p class={styles["opt-what"]}>{option().treatments.join(", ")}</p>
              </Show>
              <Show when={option().visits > 0}>
                <p class={styles["opt-meta"]}>
                  {words().t("visits", { count: option().visits })}
                  <Show when={option().minutes}>
                    {(minutes) => (
                      <> · {words().t("inTheChair", { time: words().chairTime(minutes()) })}</>
                    )}
                  </Show>
                </p>
              </Show>
              <Points icon="check" label={words().t("goodToKnow")} points={option().upsides} />
              <Points
                icon="info"
                label={words().t("keepInMind")}
                points={option().considerations}
              />
            </li>
          )}
        </Index>
      </ol>
    </>
  );
}

function Points(props: { icon: "check" | "info"; label: string; points: string[] }) {
  return (
    <Show when={props.points.length > 0}>
      <div class={styles["opt-pts"]} data-kind={props.icon}>
        <div class={styles["opt-k"]}>{props.label}</div>
        <ul>
          <For each={props.points}>
            {(point) => (
              <li>
                <Icon name={props.icon} size="none" aria-hidden="true" />
                <span>{point}</span>
              </li>
            )}
          </For>
        </ul>
      </div>
    </Show>
  );
}
